import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { setupRouterGuards } from './guards'

// Developer-tool onboarding preview pages (join/preview/*).
// These are NOT part of the production navigation surface. They are gated two ways
// (defense-in-depth, mirroring CLAUDE.md §2e "code-layer gate, not config-only"):
//   1. Statically excluded from the route table in production builds (import.meta.env.DEV
//      is a compile-time constant → `false` in prod → tree-shaken out entirely).
//   2. Tagged `meta.devOnly` so the router guard hard-blocks them even if a build ever
//      ships them by mistake.
const devPreviewRoutes: RouteRecordRaw[] = import.meta.env.DEV
  ? [
      {
        path: 'join/preview/access',
        name: 'join-preview-access',
        component: () => import('@/pages/join/preview/PreviewAccessPage.vue'),
        meta: { title: 'Preview Access', devOnly: true },
      },
      {
        path: 'join/preview/account-opening-status',
        name: 'join-preview-account-opening-status',
        component: () => import('@/pages/join/preview/PreviewAccountOpeningStatusPage.vue'),
        meta: { title: 'Preview Account Opening Status', devOnly: true },
      },
      {
        path: 'join/preview/broker-open-account-info',
        name: 'join-preview-broker-open-account-info',
        component: () => import('@/pages/join/preview/PreviewBrokerOpenAccountInfoPage.vue'),
        meta: { title: 'Preview Broker Open Account Info', devOnly: true },
      },
      {
        path: 'join/preview/contract',
        name: 'join-preview-contract',
        component: () => import('@/pages/join/preview/PreviewContractPage.vue'),
        meta: { title: 'Preview Contract', devOnly: true },
      },
      {
        path: 'join/preview/join',
        name: 'join-preview-join',
        component: () => import('@/pages/join/preview/PreviewJoinPage.vue'),
        meta: { title: 'Preview Join', devOnly: true },
      },
      {
        path: 'join/preview/markdown-contract',
        name: 'join-preview-markdown-contract',
        component: () => import('@/pages/join/preview/PreviewMarkdownContractPage.vue'),
        meta: { title: 'Preview Markdown Contract', devOnly: true },
      },
    ]
  : []

const routes: RouteRecordRaw[] = [
  // Public routes
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { title: 'Login' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
    meta: { title: 'Forgot Password' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/auth/ResetPasswordPage.vue'),
    meta: { title: 'Reset Password' },
  },
  // LINE LIFF routes
  {
    path: '/auth/liff',
    name: 'liff',
    component: () => import('@/pages/auth/LiffPage.vue'),
    meta: { title: 'LINE Login' },
  },
  {
    path: '/auth/signup',
    name: 'signup',
    component: () => import('@/pages/auth/SignupPage.vue'),
    meta: { title: 'LINE Signup' },
  },

  // Protected routes (main layout)
  {
    path: '/',
    component: () => import('@/pages/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'profile',
        redirect: { name: 'settings' },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/settings/SettingsPage.vue'),
        meta: { title: '個人設定' },
      },
      // Join (onboarding) routes
      {
        path: 'join/status',
        name: 'join-status',
        component: () => import('@/pages/join/StatusPage.vue'),
        meta: { title: 'Onboarding Status' },
      },
      {
        path: 'join',
        component: () => import('@/pages/join/JoinWizardLayout.vue'),
        children: [
          {
            path: 'personal-info',
            name: 'join-personal-info',
            component: () => import('@/pages/join/PersonalInfoPage.vue'),
            meta: { title: 'Personal Information', joinStep: 0 },
          },
          {
            path: 'questionnaire',
            name: 'join-questionnaire',
            component: () => import('@/pages/join/QuestionnairePage.vue'),
            meta: { title: 'Questionnaire', joinStep: 1 },
          },
          {
            path: 'contract',
            name: 'join-contract',
            component: () => import('@/pages/join/ContractPage.vue'),
            meta: { title: 'Contract', joinStep: 2 },
          },
          {
            path: 'account-opening',
            name: 'join-account-opening',
            component: () => import('@/pages/join/AccountOpeningPage.vue'),
            meta: { title: 'Account Opening', joinStep: 3 },
          },
          {
            path: 'broker-activation',
            name: 'join-broker-activation',
            component: () => import('@/pages/join/BrokerActivationPage.vue'),
            meta: { title: '券商開通', joinStep: 3 },
          },
          {
            path: 'acceptance-report',
            name: 'join-acceptance-report',
            component: () => import('@/pages/join/AcceptanceReportPage.vue'),
            meta: { title: '驗收報告簽署', joinStep: 4 },
          },
          {
            path: 'phone-confirmation',
            name: 'join-phone-confirmation',
            component: () => import('@/pages/join/PhoneConfirmationPage.vue'),
            meta: { title: '電話確認預約', joinStep: 5 },
          },
        ],
      },

      // Join preview routes (developer tools) — dev-only, see devPreviewRoutes above.
      // Empty array in production builds (tree-shaken); guard also hard-blocks meta.devOnly.
      ...devPreviewRoutes,

      // Trading routes
      {
        path: 'trading',
        component: () => import('@/pages/trading/TradingHubPage.vue'),
        children: [
          {
            path: '',
            redirect: { name: 'trading-overview' },
          },
          {
            path: 'overview',
            name: 'trading-overview',
            component: () => import('@/pages/trading/OverviewPage.vue'),
            meta: { title: 'Trading Overview' },
          },
          {
            path: 'positions',
            name: 'trading-positions',
            component: () => import('@/pages/trading/PositionsPage.vue'),
            meta: { title: 'Positions' },
          },
          {
            path: 'profit',
            name: 'trading-profit',
            component: () => import('@/pages/trading/ProfitPage.vue'),
            meta: { title: 'Profit & Loss' },
          },
          {
            path: 'guide',
            component: () => import('@/pages/trading/PerformanceGuidePage.vue'),
            meta: { title: 'Performance Guide' },
            children: [
              {
                // Overview is rendered by the parent hub itself (isOverview); this
                // empty child only provides the named default route so the parent's
                // <router-view> stays empty on the 概覽 tab (no self-nesting).
                path: '',
                name: 'trading-guide',
                component: { render: () => null },
                meta: { title: '績效概覽' },
              },
              {
                path: 'strategy',
                name: 'trading-guide-strategy',
                component: () => import('@/pages/trading/StrategyPerformancePage.vue'),
                meta: { title: '策略績效' },
              },
              {
                path: 'product',
                name: 'trading-guide-product',
                component: () => import('@/pages/trading/ProductPerformancePage.vue'),
                meta: { title: '產品績效' },
              },
            ],
          },
          {
            path: 'history',
            name: 'trading-history',
            component: () => import('@/pages/trading/HistoryChartPage.vue'),
            meta: { title: 'History Charts' },
          },
          {
            path: 'report',
            name: 'trading-report',
            component: () => import('@/pages/trading/ReportPage.vue'),
            meta: { title: 'Performance Report' },
          },
          {
            path: 'report/print',
            name: 'trading-report-print',
            component: () => import('@/pages/trading/ReportPrintPage.vue'),
            meta: { title: 'Print Report', hideTabBar: true },
          },
          {
            path: 'strategy',
            name: 'trading-strategy',
            component: () => import('@/pages/trading/StrategyPage.vue'),
            meta: { title: '策略列表' },
          },
          {
            path: 'strategy/:id',
            name: 'trading-strategy-detail',
            component: () => import('@/pages/trading/StrategyDetailPage.vue'),
            meta: { title: '策略詳情' },
          },
        ],
      },
      {
        path: 'trading/settings',
        name: 'trading-settings',
        component: () => import('@/pages/trading/TradingSettingsPage.vue'),
        meta: { title: '交易設定' },
      },

      // Accounting routes
      {
        path: 'accounting/bills',
        name: 'accounting-bills',
        component: () => import('@/pages/accounting/BillsPage.vue'),
        meta: { title: 'Bills' },
      },
      {
        path: 'accounting/payment',
        name: 'accounting-payment',
        component: () => import('@/pages/accounting/PaymentPage.vue'),
        meta: { title: 'Payment' },
      },
      {
        path: 'accounting/payment/:id',
        name: 'accounting-payment-detail',
        component: () => import('@/pages/accounting/PaymentDetailPage.vue'),
        meta: { title: 'Payment Detail' },
      },

      // Credentials route
      {
        path: 'trading/credentials',
        name: 'trading-credentials',
        component: () => import('@/pages/trading/UpdateCredPage.vue'),
        meta: { title: 'Credentials' },
      },

      // Support tickets route (customer)
      {
        path: 'support/tickets',
        name: 'support-tickets',
        component: () => import('@/pages/support/TicketsPage.vue'),
        meta: { title: '我的工單' },
      },
    ],
  },

  // Sales routes (for salesmen)
  {
    path: '/sales',
    component: () => import('@/pages/layouts/SalesLayout.vue'),
    meta: {
      requiresAuth: true,
      roles: ['salesman'],
    },
    children: [
      {
        path: '',
        redirect: { name: 'sales-dashboard' },
      },
      {
        path: 'dashboard',
        name: 'sales-dashboard',
        component: () => import('@/pages/sales/DashboardPage.vue'),
        meta: { title: 'Sales Dashboard' },
      },
      {
        path: 'customers',
        name: 'sales-customers',
        component: () => import('@/pages/sales/CustomersPage.vue'),
        meta: { title: 'My Customers' },
      },
      {
        path: 'invite',
        name: 'sales-invite',
        component: () => import('@/pages/sales/InvitePage.vue'),
        meta: { title: 'Invite Customers' },
      },
      {
        path: 'commissions',
        name: 'sales-commissions',
        component: () => import('@/pages/sales/CommissionsPage.vue'),
        meta: { title: 'Commission Reports' },
      },
      {
        path: 'tickets',
        name: 'sales-tickets',
        component: () => import('@/pages/sales/TicketsPage.vue'),
        meta: { title: '客戶工單' },
      },
    ],
  },

  // Reseller routes
  {
    path: '/reseller',
    component: () => import('@/pages/layouts/ResellerLayout.vue'),
    meta: {
      requiresAuth: true,
      roles: ['main_reseller', 'sub_reseller'],  // Salesmen now use /sales routes
    },
    children: [
      {
        path: '',
        redirect: { name: 'reseller-dashboard' },
      },
      {
        path: 'dashboard',
        name: 'reseller-dashboard',
        component: () => import('@/pages/reseller/DashboardPage.vue'),
        meta: { title: 'Reseller Dashboard' },
      },
      {
        path: 'customers',
        name: 'reseller-customers',
        component: () => import('@/pages/reseller/CustomersPage.vue'),
        meta: { title: 'Customers' },
      },
      {
        path: 'customers/:id',
        name: 'reseller-customer-detail',
        component: () => import('@/pages/reseller/CustomerDetailPage.vue'),
        meta: { title: '客戶詳情' },
      },
      {
        path: 'acceptance-reports',
        name: 'reseller-acceptance-reports',
        component: () => import('@/pages/reseller/AcceptanceReportsPage.vue'),
        meta: { title: 'Acceptance Reports' },
      },
      {
        path: 'phone-records',
        name: 'reseller-phone-records',
        component: () => import('@/pages/reseller/PhoneRecordsPage.vue'),
        meta: { title: 'Phone Records' },
      },
      {
        path: 'salesmen',
        name: 'reseller-salesmen',
        component: () => import('@/pages/reseller/SalesmenPage.vue'),
        meta: { title: 'Salesmen' },
      },
      {
        path: 'salesmen/:id',
        name: 'reseller-salesman-detail',
        component: () => import('@/pages/reseller/SalesmanDetailPage.vue'),
        meta: { title: '業務人員詳情' },
      },
      {
        path: 'commissions',
        name: 'reseller-commissions',
        component: () => import('@/pages/reseller/CommissionsPage.vue'),
        meta: { title: 'Commissions' },
      },
      {
        path: 'checkout',
        name: 'reseller-checkout',
        component: () => import('@/pages/reseller/BillingCheckoutPage.vue'),
        meta: { title: 'Billing Checkout' },
      },
      {
        path: 'coupons',
        name: 'reseller-coupons',
        component: () => import('@/pages/reseller/CouponsPage.vue'),
        meta: { title: 'Coupons' },
      },
      {
        path: 'profile',
        name: 'reseller-profile',
        component: () => import('@/pages/reseller/ProfilePage.vue'),
        meta: { title: 'Reseller Profile' },
      },
      {
        path: 'charge-policies',
        name: 'reseller-charge-policies',
        component: () => import('@/pages/reseller/ChargePoliciesPage.vue'),
        meta: { title: 'Charge Policies' },
      },
      {
        path: 'customer-steps',
        name: 'reseller-customer-steps',
        component: () => import('@/pages/reseller/CustomerStepsPage.vue'),
        meta: { title: 'Customer Steps' },
      },
      {
        path: 'billing',
        name: 'reseller-billing',
        component: () => import('@/pages/reseller/BillingManagePage.vue'),
        meta: { title: 'Billing Management' },
      },
      {
        path: 'payment',
        name: 'reseller-payment',
        component: () => import('@/pages/reseller/BillingPaymentPage.vue'),
        meta: { title: 'Payment Confirmation' },
      },
      {
        path: 'sub-resellers',
        name: 'reseller-sub-resellers',
        component: () => import('@/pages/reseller/SubResellersPage.vue'),
        meta: { title: 'Sub Resellers' },
      },
      {
        path: 'demo-tokens',
        name: 'reseller-demo-tokens',
        component: () => import('@/pages/reseller/DemoTokensPage.vue'),
        meta: { title: 'Demo Tokens' },
      },
      {
        path: 'activity-log',
        name: 'reseller-activity-log',
        component: () => import('@/pages/reseller/ActivityLogPage.vue'),
        meta: { title: 'Activity Log' },
      },
      {
        path: 'sub-reseller/:id',
        name: 'reseller-sub-reseller-detail',
        component: () => import('@/pages/reseller/SubResellerDetailPage.vue'),
        meta: { title: 'Sub-Reseller Detail' },
      },
      {
        path: 'sub-reseller-account/:id',
        name: 'reseller-sub-reseller-account',
        component: () => import('@/pages/reseller/SubResellerAccountPage.vue'),
        meta: { title: 'Sub-Reseller Account' },
      },
      {
        path: 'customer-account/:id',
        name: 'reseller-customer-account',
        component: () => import('@/pages/reseller/CustomerAccountPage.vue'),
        meta: { title: 'Customer Account' },
      },
      {
        path: 'account-edit/:id',
        name: 'reseller-account-edit',
        component: () => import('@/pages/reseller/AccountEditPage.vue'),
        meta: { title: 'Account Edit' },
      },
      {
        path: 'modify-account/:id',
        name: 'reseller-modify-account',
        component: () => import('@/pages/reseller/ModifyAccountPage.vue'),
        meta: { title: 'Modify Account' },
      },
      {
        path: 'modify-records',
        name: 'reseller-modify-records',
        component: () => import('@/pages/reseller/ModifyRecordPage.vue'),
        meta: { title: 'Modify Records' },
      },
      {
        path: 'reseller-account',
        name: 'reseller-account',
        component: () => import('@/pages/reseller/ResellerAccountPage.vue'),
        meta: { title: 'Reseller Account' },
      },
      {
        path: 'more',
        name: 'reseller-more',
        component: () => import('@/pages/reseller/MorePage.vue'),
        meta: { title: 'More' },
      },
      {
        path: 'erp',
        name: 'reseller-erp',
        component: () => import('@/pages/reseller/ErpPage.vue'),
        meta: { title: 'ERP Integration' },
      },
      {
        path: 'referrals',
        name: 'reseller-referrals',
        component: () => import('@/pages/reseller/ReferralsPage.vue'),
        meta: { title: 'Referrals' },
      },
    ],
  },

  // Admin routes
  {
    path: '/admin',
    component: () => import('@/pages/layouts/AdminLayout.vue'),
    meta: {
      requiresAuth: true,
      roles: ['site_admin', 'site_owner'],  // 對應後端 snake_case 回傳
    },
    children: [
      {
        path: '',
        redirect: { name: 'admin-dashboard' },
      },
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('@/pages/admin/DashboardPage.vue'),
        meta: { title: 'Admin Dashboard' },
      },
      {
        path: 'resellers',
        name: 'admin-resellers',
        component: () => import('@/pages/admin/ResellersPage.vue'),
        meta: { title: 'Resellers' },
      },
      {
        path: 'tickets',
        name: 'admin-tickets',
        component: () => import('@/pages/admin/TicketsPage.vue'),
        meta: { title: 'Support Tickets' },
      },
      {
        path: 'settings',
        name: 'admin-settings',
        component: () => import('@/pages/admin/SettingsPage.vue'),
        meta: { title: 'Settings' },
      },
      {
        path: 'email-config',
        name: 'admin-email-config',
        component: () => import('@/pages/admin/EmailConfigPage.vue'),
        meta: { title: 'Email Config' },
      },
      {
        path: 'erp-customers',
        name: 'admin-erp-customers',
        component: () => import('@/pages/admin/ErpCustomerPage.vue'),
        meta: { title: 'ERP Customers' },
      },
      {
        path: 'erp-site',
        name: 'admin-erp-site',
        component: () => import('@/pages/admin/ErpSitePage.vue'),
        meta: {
          title: 'ERP Site Config',
          roles: ['site_admin', 'site_owner'],
        },
      },
    ],
  },

  {
    path: '/identity/switch',
    name: 'identity-switch-confirm',
    component: () => import('@/pages/identity/IdentitySwitchConfirmPage.vue'),
    meta: { title: '切換身分', requiresAuth: true },
  },

  // Error routes
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/pages/error/ForbiddenPage.vue'),
    meta: { title: 'Access Denied' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/error/NotFoundPage.vue'),
    meta: { title: 'Page Not Found' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

setupRouterGuards(router)

export default router
